import React, { useCallback, useEffect, useRef, useState, memo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { getGuestMessagesForOwner, updateGuestMessageStatus, deleteGuestMessage, getDraft } from '@/src/infrastructure/lib/api';
import type { GuestMessage } from '@/src/types';
import { Button } from '@/src/modules/components/Button';
import { Check, X, Trash2, ArrowLeft, MessageSquare, Download } from 'lucide-react';


interface BlessingRowProps {
  msg: GuestMessage;
  isBusy: boolean;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onDelete: (id: string) => void;
}

const BlessingRow = memo(function BlessingRow({ msg, isBusy, onApprove, onReject, onDelete }: BlessingRowProps) {
  return (
    <tr className="hover:bg-surface-variant/5 transition-colors">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          {msg.photoUrl ? (
            <img
              src={msg.photoUrl}
              alt={msg.guestName}
              className="h-10 w-10 rounded-full object-cover border border-outline-variant/30"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-sm font-bold text-primary">
              {msg.guestName.charAt(0).toUpperCase()}
            </div>
          )}
          <span className="font-bold text-on-surface">{msg.guestName}</span>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="inline-flex items-center rounded-full bg-surface-variant/40 px-2.5 py-1 text-xs font-semibold text-on-surface-variant">
          {msg.relationship || 'Guest'}
        </span>
      </td>
      <td className="px-6 py-4 max-w-xs sm:max-w-md">
        <p className="text-on-surface font-medium leading-relaxed break-words">{msg.message}</p>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {msg.isApproved ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-1 text-xs font-bold text-emerald-600 border border-emerald-500/20">
            Approved
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-bold text-amber-600 border border-amber-500/20">
            Pending
          </span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-xs text-on-surface-variant">
        {new Date(msg.createdAt).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="flex items-center justify-end gap-2">
          {msg.isApproved ? (
            <button
              onClick={() => onReject(msg.id)}
              disabled={isBusy}
              className="p-2 rounded-xl border border-outline-variant/30 text-amber-600 hover:bg-amber-50/50 disabled:opacity-50 transition-colors"
              title="Reject / Hide from wall"
            >
              <X size={16} />
            </button>
          ) : (
            <button
              onClick={() => onApprove(msg.id)}
              disabled={isBusy}
              className="p-2 rounded-xl border border-outline-variant/30 text-emerald-600 hover:bg-emerald-50/50 disabled:opacity-50 transition-colors"
              title="Approve & Show on wall"
            >
              <Check size={16} />
            </button>
          )}
          <button
            onClick={() => onDelete(msg.id)}
            disabled={isBusy}
            className="p-2 rounded-xl border border-outline-variant/30 text-status-failed hover:bg-status-failed/5 disabled:opacity-50 transition-colors"
            title="Delete permanently"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
});

export function BlessingsManagementPage() {
  const { invitationId = '' } = useParams<{ invitationId: string }>();
  const [searchParams] = useSearchParams();
  const isPrintMode = searchParams.get('print') === 'true';
  const printTriggered = useRef(false);

  const [messages, setMessages] = useState<GuestMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [actionBusyId, setActionBusyId] = useState<string | null>(null);
  const [eventName, setEventName] = useState<string>('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, draft] = await Promise.all([
        getGuestMessagesForOwner(invitationId),
        getDraft(invitationId).catch(() => null)
      ]);
      setMessages(data);
      if (draft && draft.eventName) {
        setEventName(draft.eventName);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [invitationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Print mode: auto-trigger window.print() once messages have loaded.
  useEffect(() => {
    if (isPrintMode && !loading && !printTriggered.current) {
      printTriggered.current = true;
      const originalTitle = document.title;
      if (eventName) {
        document.title = `${eventName} wishedwall`;
      } else {
        document.title = 'wishedwall';
      }
      const timer = setTimeout(() => {
        window.print();
        document.title = originalTitle;
        window.close();
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [isPrintMode, loading, eventName]);

  const handleDownloadPdf = () => {
    const url = `/dashboard/invitations/${invitationId}/blessings?print=true`;
    window.open(url, '_blank');
  };

  const showToast = useCallback((msg: string) => {

    setToast(msg);
    setTimeout(() => setToast(null), 3000);

  }, []);

  const handleApprove = useCallback(async (id: string) => {

    setActionBusyId(id);
    try {
      await updateGuestMessageStatus(id, { isApproved: true });
      showToast('Blessing approved successfully');
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, isApproved: true } : m))
      );
    } catch (e) {
      showToast(`Failed: ${(e as Error).message}`);
    } finally {
      setActionBusyId(null);
    }
  }, [showToast]);

  const handleReject = useCallback(async (id: string) => {

    setActionBusyId(id);
    try {
      await updateGuestMessageStatus(id, { isApproved: false });
      showToast('Blessing rejected / hidden successfully');
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, isApproved: false } : m))
      );
    } catch (e) {
      showToast(`Failed: ${(e as Error).message}`);
    } finally {
      setActionBusyId(null);
    }
  }, [showToast]);

  const handleDelete = useCallback(async (id: string) => {

    if (!window.confirm('Are you sure you want to permanently delete this blessing?')) {
      return;
    }
    setActionBusyId(id);
    try {
      await deleteGuestMessage(id);
      showToast('Blessing deleted permanently');
      setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      showToast(`Failed: ${(e as Error).message}`);
    } finally {
      setActionBusyId(null);
    }
  }, [showToast]);

  // Stats calculation
  const total = messages.length;

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const addedToday = messages.filter((m) => {
    const createdDate = new Date(m.createdAt);
    return createdDate >= todayStart;
  }).length;

  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const addedThisWeek = messages.filter((m) => {
    const createdDate = new Date(m.createdAt);
    return createdDate >= sevenDaysAgo;
  }).length;

  const guestCounts: Record<string, number> = {};
  messages.forEach((m) => {
    const name = m.guestName.trim();
    if (name) {
      guestCounts[name] = (guestCounts[name] || 0) + 1;
    }
  });

  const sortedGuests = Object.entries(guestCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);


  // Print-mode view: clean printable grid, no nav/actions
  if (isPrintMode) {
    return (
      <div style={{ fontFamily: 'system-ui, sans-serif', padding: '24px' }}>
        <style>{`
          @media print {
            button, [data-no-print] { display: none !important; }
            body { margin: 0; }
          }
        `}</style>
        <h1 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '4px' }}>Guest Messages</h1>
        <p style={{ fontSize: '12px', color: '#666', marginBottom: '24px' }}>
          Printed on {new Date().toLocaleDateString(undefined, { dateStyle: 'long' })}
        </p>
        {loading ? (
          <p>Loading…</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Guest</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Relationship</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Message</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Date</th>
              </tr>
            </thead>
            <tbody>
              {messages.map((msg, i) => (
                <tr key={msg.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>{msg.guestName}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>{msg.relationship || 'Guest'}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', maxWidth: '320px', wordBreak: 'break-word' }}>{msg.message}</td>
                  <td style={{ padding: '8px 12px', borderBottom: '1px solid #e5e7eb', color: '#6b7280', whiteSpace: 'nowrap' }}>
                    {new Date(msg.createdAt).toLocaleDateString(undefined, { dateStyle: 'medium' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <p style={{ marginTop: '32px', fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
          Made with InviteCloud.in
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-8 space-y-6">
      {/* Toast notification */}
      {toast && (
        <div className="fixed bottom-5 right-5 z-50 rounded-2xl bg-on-surface text-surface px-5 py-3 shadow-xl text-sm font-semibold transition-all">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <Link
            to="/dashboard/invitations"
            className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-primary hover:underline"
          >
            <ArrowLeft size={14} /> Back to dashboard
          </Link>
          <h1 className="text-2xl font-black text-on-surface sm:text-3xl flex items-center gap-2">
            <MessageSquare className="text-primary" size={28} /> Guest Messages Wall
          </h1>
          <p className="text-sm text-on-surface-variant">
            Moderate and view public blessings left by your wedding guests.
          </p>
        </div>
        <div className="flex items-center gap-2 sm:self-end">
          <Button
            onClick={handleDownloadPdf}
            disabled={loading || messages.length === 0}
            variant="outline"
            className="flex items-center gap-2"
          >
            <Download size={14} /> Download PDF
          </Button>
          <Button onClick={refresh} disabled={loading} variant="outline">
            Refresh List
          </Button>
        </div>
      </div>

      {/* Stats Summary Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 rounded-3xl border border-outline-variant/20 bg-surface p-6 shadow-sm">
        <div className="text-center border-b sm:border-b-0 sm:border-r border-outline-variant/30 pb-4 sm:pb-0 flex flex-col justify-center">
          <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Total Blessings</p>
          <p className="mt-1 text-2xl font-black text-on-surface tabular-nums">{total}</p>
        </div>
        <div className="text-center border-b sm:border-b-0 sm:border-r border-outline-variant/30 pb-4 sm:pb-0 flex flex-col justify-center">
          <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">Added Today</p>
          <p className="mt-1 text-2xl font-black text-amber-500 tabular-nums">{addedToday}</p>
        </div>
        <div className="text-center border-b sm:border-b-0 sm:border-r border-outline-variant/30 pb-4 sm:pb-0 flex flex-col justify-center">
          <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">This Week</p>
          <p className="mt-1 text-2xl font-black text-emerald-500 tabular-nums">{addedThisWeek}</p>
        </div>
        <div className="text-center flex flex-col items-center justify-center pt-4 sm:pt-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant mb-1">Most Active Guests</p>
          {sortedGuests.length > 0 ? (
            <div className="flex flex-wrap gap-1 justify-center max-w-full">
              {sortedGuests.map(([name, count]) => (
                <span
                  key={name}
                  className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary max-w-[150px] truncate"
                  title={`${name}: ${count} blessings`}
                >
                  {name} ({count})
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm font-semibold text-on-surface-variant italic mt-1">No data yet</p>
          )}
        </div>
      </div>

      {/* Content Area */}
      {loading && messages.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-3xl border border-outline-variant/15 bg-surface/50">
          <div className="text-sm font-semibold text-on-surface-variant">Loading guest messages...</div>
        </div>
      ) : error ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 rounded-3xl border border-outline-variant/15 bg-surface/50 text-center">
          <p className="text-sm font-semibold text-status-failed">Failed to load messages: {error}</p>
          <Button onClick={refresh} variant="outline" size="sm">
            Try Again
          </Button>
        </div>
      ) : messages.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-3xl border border-outline-variant/15 bg-surface/50 text-center">
          <p className="text-sm font-bold text-on-surface">No blessings received yet</p>
          <p className="text-xs text-on-surface-variant">Guest submissions will appear here once guests submit the blessing form.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-3xl border border-outline-variant/20 bg-surface shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-outline-variant/20 bg-surface-variant/10 text-xs font-bold uppercase tracking-wider text-on-surface-variant/75">
                  <th className="px-6 py-4">Guest</th>
                  <th className="px-6 py-4">Relationship</th>
                  <th className="px-6 py-4">Message</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4">Date</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/10">
                {messages.map((msg) => (
                  <BlessingRow
                    key={msg.id}
                    msg={msg}
                    isBusy={actionBusyId === msg.id}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    onDelete={handleDelete}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
